import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import { loadErrorMessage } from "shared/loadOrError";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";
import { canUseDataMapper } from "../rbac";
import {
  dataMapperSelection,
  dataMapperService,
  type DataMapperServicePath,
} from "../dataMapperServices";
import {
  DATA_MAPPER_SCOPES,
  refusalReason,
  sameId,
  visibleToSelection,
  type DataMapperLookupTable,
  type DataMapperProfile,
  type DataMapperSchemaEntry,
  type DataMapperScope,
} from "../dataMapper.types";
import { GONE_REASONS } from "../shared/editorSupport";

export interface DataMapperLibraryGroup {
  scope: DataMapperScope;
  schemas: DataMapperSchemaEntry[];
  lookupTables: DataMapperLookupTable[];
}


/**
 * Data Mapper → Library: the schemas and lookup tables the OPEN profile sees,
 * grouped by scope (its own, its client's, global). Read-only in this slice —
 * it exists to prove the selection plumbing (plan §2.4): every find names the
 * selection from DataMapperViewStore, and the lists are computed views over the
 * two ServiceStores' caches, filtered to that selection.
 */
class DataMapperLibraryStore {
  rootStore: RootStore;

  loading = false;
  loaded = false;
  error: string | null = null;

  private loadSeq = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  private get selection() {
    return dataMapperSelection(this.rootStore);
  }

  get canUse(): boolean {
    return canUseDataMapper(this.rootStore);
  }

  get clientId(): string | null {
    return this.selection?.selectedClientId ?? null;
  }

  get profileId(): string | null {
    return this.selection?.selectedProfileId ?? null;
  }

  /** The open profile's record, looked up in the profiles ServiceStore (never copied here). */
  get profile(): DataMapperProfile | null {
    const id = this.profileId;
    if (!id) return null;
    const all = (dataMapperService(this.rootStore, "data-mapper-profiles")?.entities ??
      []) as unknown as DataMapperProfile[];
    return all.find((p) => sameId(p.id, id)) ?? null;
  }

  get schemas(): DataMapperSchemaEntry[] {
    const rows = (dataMapperService(this.rootStore, "data-mapper-schemas")?.entities ??
      []) as unknown as DataMapperSchemaEntry[];
    if (!this.profileId) return [];
    return rows.filter((r) => visibleToSelection(r, this.clientId, this.profileId));
  }

  get lookupTables(): DataMapperLookupTable[] {
    const rows = (dataMapperService(this.rootStore, "data-mapper-lookup-tables")?.entities ??
      []) as unknown as DataMapperLookupTable[];
    if (!this.profileId) return [];
    return rows.filter((r) => visibleToSelection(r, this.clientId, this.profileId));
  }

  /** One group per scope, narrowest first; each list sorted by name. */
  get groups(): DataMapperLibraryGroup[] {
    const byName = <T extends { name: string }>(a: T, b: T) => a.name.localeCompare(b.name);
    return DATA_MAPPER_SCOPES.map((scope) => ({
      scope,
      schemas: this.schemas.filter((s) => s.scope === scope).sort(byName),
      lookupTables: this.lookupTables.filter((t) => t.scope === scope).sort(byName),
    }));
  }

  /**
   * Load the open profile's schemas and lookup tables (and its client's
   * profiles, so the header can name it after a reload). Every call names the
   * selection. A selection the API no longer recognises is dropped, so the
   * page says "open a profile" rather than repeating the refusal.
   */
  async load(): Promise<void> {
    const seq = ++this.loadSeq;
    const selection = this.selection;
    const authStore = (
      this.rootStore as unknown as { authStore?: { currentUser?: { id?: string | number } } }
    ).authStore;
    selection?.bindToUser(authStore?.currentUser?.id);
    if (!this.canUse || !selection?.hasProfile) {
      this.loading = false;
      this.loaded = false;
      this.error = null;
      return;
    }
    const query = { ...selection.selectionQuery };
    this.loading = true;
    this.error = null;
    // The stores are `throwOnError: true`, so a refusal rejects and a null
    // result is only a superseded find (not findOrThrow — see the Profiles
    // store). allSettled, not all: a first rejection must not mark the page
    // loaded while the other finds are still landing in their caches (Codex r1).
    const find = (path: DataMapperServicePath, q: Record<string, unknown>) => {
      const service = dataMapperService(this.rootStore, path);
      return service
        ? service.find({ query: q })
        : Promise.reject(new Error("The Data Mapper service is not available in this build."));
    };
    // The reset after a sign-out bumps loadSeq, but the epoch moves first: a
    // settle in between (one find failed on its own, the others were ended)
    // would still pass the seq check — and latch `loaded` over a reset store.
    const ended = sessionMark(this.rootStore);
    const results = await Promise.allSettled([
      find("data-mapper-schemas", query),
      find("data-mapper-lookup-tables", query),
      find("data-mapper-profiles", { clientId: query.clientId }),
    ]);
    const failure = results.find((r): r is PromiseRejectedResult => r.status === "rejected");
    runInAction(() => {
      if (seq !== this.loadSeq || ended()) return;
      this.loading = false;
      this.loaded = true;
      if (!failure || isSessionEnded(failure.reason)) return;
      const err: unknown = failure.reason;
      if (GONE_REASONS.has(refusalReason(err) ?? "") && query.profileId) {
        selection.forgetProfile(query.profileId);
        this.error = "The profile that was open no longer exists. Choose one on the Profiles page.";
      } else {
        this.error = loadErrorMessage(err, "The schemas and lookup tables could not be loaded.");
      }
    });
  }

  reset() {
    this.loadSeq++;
    this.loading = false;
    this.loaded = false;
    this.error = null;
  }
}

export default DataMapperLibraryStore;
